/**
 * Mock authentication service.
 *
 * Honours the AuthService interface with in-memory state. It performs no real
 * authentication and MUST NOT be shipped to a real environment — the registry
 * (`../index.js`) is where that swap happens: a Firebase-backed service is used
 * when Firebase configuration is present, and this mock otherwise (Phase 1
 * prototype, and the accessibility/e2e build which has no backend).
 *
 * Even in the mock, no password is stored, echoed, or logged. That is the
 * behaviour the real service must have, and building the habit here keeps the
 * interface honest.
 */

import { makeDemoAccount } from "./fixtures.js";

/**
 * @returns {import("../interfaces.js").AuthService}
 */
export function createMockAuthService() {
  /** @type {import("../../types/models.js").Account | null} */
  let account = null;

  /** @type {Set<() => void>} */
  const listeners = new Set();

  function notify() {
    for (const listener of listeners) {
      listener();
    }
  }

  return {
    getCurrentAccount() {
      return account;
    },

    // The mock knows its state synchronously, so it is ready from the outset. A
    // real backend is not (see the interface note); the router relies on this
    // being immediate to avoid a needless loading state in the prototype build.
    isReady() {
      return true;
    },

    async whenReady() {},

    async signIn(email, _password) {
      // A mock: any non-empty credentials "succeed". Validation of the real
      // kind is done by the Firebase service. The password is deliberately
      // ignored, never retained.
      if (!email) {
        throw new Error("Enter your email address to sign in.");
      }
      account = { ...makeDemoAccount(), email };
      notify();
      return account;
    },

    async register(email, _password) {
      if (!email) {
        throw new Error("Enter your email address to register.");
      }
      // A freshly registered account starts unverified and without a plan.
      account = {
        ...makeDemoAccount(),
        email,
        emailVerified: false,
        subscriptionStatus: "none",
      };
      notify();
      return account;
    },

    async signOut() {
      account = null;
      notify();
    },

    async sendVerificationEmail() {
      if (!account) {
        throw new Error("Sign in to resend a verification email.");
      }
    },

    async sendPasswordReset(_email) {
      // Deliberately reveals nothing about whether the address is registered.
    },

    async refresh() {
      // In the prototype there is nothing to re-read; a real backend reloads the
      // user to pick up a verification completed elsewhere.
      notify();
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
